import { Box, IconButton, InputAdornment, TextField, styled, useTheme } from '@mui/material';
import { Column, Row } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ReactComponent as ClearIcon } from '../../../../assets/icons/close-grey.svg';
import { ReactComponent as FileCopyIcon } from '../../../../assets/icons/file_copy.svg';
import { useLoadingBar } from '../../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { useUpdateTimesheetCell } from '../../../../queries/hooks/useUpdateTimesheetCell';
import { LoadingId, TimesheetRow } from '../../../../types';
import { useSingleTimesheet } from '../../../../queries/hooks/useSingleTimesheet';
import { useTimesheetRouteId } from '../../context/TimesheetRouteIdContext';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../../state/UIState';
import { Typography } from '../../../../components/Typography/Typography';

type Props = {
  getValue: () => unknown;
  row: Row<TimesheetRow>;
  column: Column<TimesheetRow>;
  table: any;
  headerDate?: string;
  weekDates?: string[];
  /** Submitted / month read-only: no edits; clipboard copy still allowed. */
  readOnly?: boolean;
};

const TIMESHEET_DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
type TimesheetDayKey = (typeof TIMESHEET_DAY_ORDER)[number];

function isTimesheetDayColumn(columnId: string): columnId is TimesheetDayKey {
  return (TIMESHEET_DAY_ORDER as readonly string[]).includes(columnId);
}

export const EditableNumberCell = ({
  getValue,
  row,
  column,
  table,
  headerDate,
  weekDates,
  readOnly = false,
}: Props) => {
  const initialValue = getValue() ?? '';
  const id = useTimesheetRouteId();
  const isOnboarding = id === 'demo';
  const { pushErrorToast, pushSuccessToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const { colors } = useTheme();

  const [showClear, setShowClear] = useState(false);

  const { mutate: onUpdateCell } = useUpdateTimesheetCell();

  const { data: singleTimesheet } = useSingleTimesheet({
    id: Number(id),
    enabled: !isOnboarding,
  });

  const monthLabel = useMemo(() => {
    if (headerDate) {
      return Number(headerDate.split(',')[1].split('/')[0]);
    }
    return 0;
  }, [headerDate]);

  const currentTSMonth = useMemo(() => {
    if (singleTimesheet?.weekDates.length) {
      return Number(singleTimesheet?.weekDates[0].split('-')[1]);
    }
    if (singleTimesheet?.holidays.length) {
      return singleTimesheet?.holidays[0].month;
    }
  }, [singleTimesheet]);

  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const emptyLabel = useMemo(() => {
    if (singleTimesheet) {
      if (headerDate) {
        if (monthLabel !== currentTSMonth) return '';
      }
      if (singleTimesheet?.weekDates.length !== 5 && singleTimesheet?.holidays.length !== 0) {
        return `N/A`;
      }
      return '';
    }
    return '';
  }, [singleTimesheet, headerDate, monthLabel, currentTSMonth]);

  const isNonWorkday = !weekDates?.some((date) => {
    const headerDateArr = headerDate?.split(',')[1].trim().split('/') ?? [];
    const headerDateFormatted = `${headerDateArr[2]}-${Number(headerDateArr[0])}-${Number(
      headerDateArr[1]
    )}`;
    return date === headerDateFormatted;
  });

  const editDisable = row.original.editDisabled || isNonWorkday;

  const displayText =
    typeof value === 'number'
      ? `${value}%`
      : emptyLabel !== ''
      ? emptyLabel
      : String(value ?? '');

  const copyTextToClipboard = useCallback(async () => {
    if (isNonWorkday && emptyLabel === 'N/A') return;
    const textToCopy =
      typeof value === 'number' && !Number.isNaN(value) ? String(value) : String(value ?? '');
    if (textToCopy === '' && emptyLabel === 'N/A') return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        pushSuccessToast({ title: 'Copied', message: 'Cell value copied to clipboard.' });
      } else {
        pushErrorToast({ message: 'Clipboard is not available in this browser.' });
      }
    } catch {
      pushErrorToast({ message: 'Could not copy to clipboard.' });
    }
  }, [value, emptyLabel, isNonWorkday, pushErrorToast, pushSuccessToast]);

  const handleUpdate = (newValue) => {
    if (readOnly) return;
    const prevValue = row.original[column.id];
    const rowId = row.original.id;

    const payload = {
      [column.id]: newValue,
    };

    if (prevValue === newValue) {
      setShowClear(false);
      return;
    }

    table.options.meta?.updateData(row.index, column.id, newValue);
    startLoading(LoadingId.updateCell);

    onUpdateCell(
      {
        id: rowId,
        payload,
      },
      {
        onSuccess: () => {
          setShowClear(false);
        },
        onError: (error: unknown) => {
          pushErrorToast({ message: extractErrorMessage(error) });
          setValue(prevValue);
          table.options.meta?.updateData(row.index, column.id, prevValue);
        },
        onSettled: () => {
          stopLoading(LoadingId.updateCell);
        },
      }
    );
  };

  const copyButton = (
    <IconButton
      size='small'
      tabIndex={-1}
      aria-label='Copy cell value'
      onClick={(e) => {
        e.stopPropagation();
        void copyTextToClipboard();
      }}
      sx={{
        p: 0.35,
        ml: 0.25,
        flexShrink: 0,
        svg: { width: 16, height: 16, path: { fill: colors.text.secondary } },
        '&:hover': { bgcolor: 'transparent', svg: { path: { fill: colors.textAccent.default } } },
      }}
    >
      <FileCopyIcon />
    </IconButton>
  );

  const isNumericZero =
    typeof value === 'number' && !Number.isNaN(value) && value === 0 && emptyLabel === '';

  const cellDomId = `ts-cell-${id}-${row.original.id}-${column.id}`;

  const handleArrowKeyNavigation = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      if (!isTimesheetDayColumn(column.id)) return;

      const keyTarget = e.target;
      if (!readOnly && keyTarget instanceof HTMLInputElement) {
        const len = keyTarget.value.length;
        const selStart = keyTarget.selectionStart ?? 0;
        const selEnd = keyTarget.selectionEnd ?? 0;
        if (e.key === 'ArrowLeft' && selStart > 0) return;
        if (e.key === 'ArrowRight' && selEnd < len) return;
      }

      const colIdx = TIMESHEET_DAY_ORDER.indexOf(column.id as TimesheetDayKey);
      if (colIdx < 0) return;

      const rows = table.getRowModel().rows;
      const rowIdx = rows.findIndex((r) => r.original.id === row.original.id);
      if (rowIdx < 0) return;

      const maxRow = rows.length - 1;

      const focusAt = (rIndex: number, dIndex: number): boolean => {
        const r = rows[rIndex];
        if (!r || dIndex < 0 || dIndex > 4) return false;
        const domId = `ts-cell-${id}-${r.original.id}-${TIMESHEET_DAY_ORDER[dIndex]}`;
        const el = document.getElementById(domId);
        if (!el) return false;
        if (el instanceof HTMLInputElement && el.disabled) return false;
        el.focus();
        if (el instanceof HTMLInputElement) el.select();
        requestAnimationFrame(() => {
          el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
        return true;
      };

      const move = (fn: () => boolean) => {
        if (fn()) {
          e.preventDefault();
        }
      };

      switch (e.key) {
        case 'ArrowLeft':
          move(() => {
            for (let d = colIdx - 1; d >= 0; d--) {
              if (focusAt(rowIdx, d)) return true;
            }
            return false;
          });
          break;
        case 'ArrowRight':
          move(() => {
            for (let d = colIdx + 1; d <= 4; d++) {
              if (focusAt(rowIdx, d)) return true;
            }
            return false;
          });
          break;
        case 'ArrowUp':
          move(() => {
            for (let r = rowIdx - 1; r >= 0; r--) {
              if (focusAt(r, colIdx)) return true;
            }
            return false;
          });
          break;
        case 'ArrowDown':
          move(() => {
            for (let r = rowIdx + 1; r <= maxRow; r++) {
              if (focusAt(r, colIdx)) return true;
            }
            return false;
          });
          break;
        default:
          break;
      }
    },
    [table, id, row.original.id, column.id, readOnly]
  );

  if (readOnly) {
    return (
      <Box
        id={cellDomId}
        tabIndex={0}
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='flex-end'
        gap={0.5}
        minHeight={40}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleArrowKeyNavigation}
        sx={{
          width: '100%',
          border: `1px solid ${colors.border.default}`,
          borderRadius: '4px',
          px: 0.75,
          py: 0.25,
          bgcolor: colors.surfaceBackground.bg1,
          outline: 'none',
        }}
      >
        <Typography
          variant='body'
          color={colors.text.main}
          style={{
            fontSize: 14,
            flex: 1,
            minWidth: 0,
            textAlign: 'right',
            opacity: isNumericZero ? 0.45 : 1,
          }}
        >
          {displayText}
        </Typography>
        {!isNonWorkday && typeof value === 'number' ? copyButton : null}
      </Box>
    );
  }

  return (
    <NumberInputField
      autoComplete='off'
      name='numberformat'
      fullWidth
      onFocus={(e) => {
        e.target.select();
      }}
      sx={{
        width: isSmallScreen ? '100px' : '100%',
        '& .MuiInputBase-input': {
          textAlign: 'right',
          ...(isNumericZero ? { opacity: 0.45 } : {}),
        },
      }}
      className='formatted-numberformat-input'
      disabled={editDisable}
      onMouseEnter={() => {
        if (editDisable) return;
        if (value !== 0) {
          setShowClear(true);
        }
      }}
      inputProps={{
        id: cellDomId,
      }}
      onKeyDown={(e) => {
        handleArrowKeyNavigation(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Backspace') {
          const newValue = value?.toString().slice(0, value?.toString().length - 1);
          setValue(Number(newValue));
        }
      }}
      onMouseLeave={() => {
        setShowClear(false);
      }}
      InputProps={{
        endAdornment:
          !isNonWorkday && !editDisable ? (
            <InputAdornment
              position='end'
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                maxHeight: 32,
                pr: 0.25,
                gap: 0.25,
              }}
            >
              {showClear ? (
                <ClearIcon
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    handleUpdate(0);
                  }}
                />
              ) : null}
              {copyButton}
            </InputAdornment>
          ) : undefined,
      }}
      size='small'
      type='string'
      value={`${typeof value === 'number' ? `${value}%` : emptyLabel}`}
      onChange={(e) => {
        const value = e.target.value.match(/[0-9]+/g)?.join('') || '0';

        setValue(Number(value));
      }}
      onBlur={(e) => {
        const value = e.target.value.match(/[0-9]+/g)?.join('') || '0';
        const newValue = Number(value);
        handleUpdate(newValue);
      }}
    />
  );
};

const NumberInputField = styled(TextField)(({ theme }) => ({
  position: 'relative',
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    display: 'none',
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: theme.colors.textAccent.disabled,
  },
  '& .MuiInputBase-root': {
    padding: 0,
    position: 'relative',
  },
}));
